import createClient, { type Client } from 'openapi-fetch';
import type { components as AuthComponents, paths as AuthPaths } from './gen/auth';
import type { components as CharacterComponents, paths as CharacterPaths } from './gen/character';
import type { components as QuestComponents, paths as QuestPaths } from './gen/quest';

export type { AuthPaths, CharacterPaths, QuestPaths };
export type AuthSchemas = AuthComponents['schemas'];
export type CharacterSchemas = CharacterComponents['schemas'];
export type QuestSchemas = QuestComponents['schemas'];

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  /** Instante (ms Unix) em que o token de acesso vence. */
  accessExpiresAt: number;
}

/** Onde a sessão fica guardada. No navegador é o `localStorage` (via @ddt/platform); nos testes, memória. */
export interface TokenStore {
  get(): Tokens | null;
  set(tokens: Tokens | null): void;
}

export class MemoryTokenStore implements TokenStore {
  private value: Tokens | null = null;
  get(): Tokens | null {
    return this.value;
  }
  set(tokens: Tokens | null): void {
    this.value = tokens;
  }
}

export interface ApiClientOptions {
  /** Raiz do Gateway com o prefixo `/api` (ex.: `http://localhost:5000/api`). */
  baseUrl: string;
  tokens: TokenStore;
  fetch?: typeof fetch;
  now?: () => number;
  /** Chamado quando a renovação falha: o refresh token não vale mais e é preciso entrar de novo. */
  onSessionExpired?: () => void;
}

/** Erro de API no formato ProblemDetails do backend (`type` leva o código estável, ex.: `validation_error`). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail: string,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(detail || code || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

interface ProblemBody {
  /** O Auth põe o código estável em `code` (extensão do problem+json); o Character, em `type`. */
  code?: string;
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

export function toApiError(status: number, body: unknown): ApiError {
  const problem = (typeof body === 'object' && body !== null ? body : {}) as ProblemBody;
  const code = problem.code ?? (problem.type && !problem.type.startsWith('http') ? problem.type : (problem.title ?? 'error'));
  return new ApiError(status, code, problem.detail ?? problem.title ?? '', problem.errors);
}

export interface MatchHistoryPlayer {
  accountId: string;
  nickName: string;
  team: 'A' | 'B';
  winner: boolean;
  damageDealt: number;
  kills: number;
  shots: number;
  hits: number;
  alive: boolean;
  forfeited: boolean;
  grade: number;
}

export interface MatchHistoryItem {
  id: string;
  mapId: number;
  ranked: boolean;
  startedAtUtc: string;
  finishedAtUtc: string;
  winnerTeam: 'A' | 'B' | null;
  reason: 'Elimination' | 'Forfeit' | 'TurnLimit';
  turns: number;
  won: boolean;
  myTeam: 'A' | 'B';
  players: MatchHistoryPlayer[];
}

const REFRESH_MARGIN_MS = 30_000;

export interface ApiClient {
  auth: Client<AuthPaths>;
  character: Client<CharacterPaths>;
  /** Missões do jogador (`/v1/quests`), pelo Gateway. */
  quest: Client<QuestPaths>;
  tokens: TokenStore;
  /** Token de acesso válido para abrir o WebSocket do Match (renova antes, se estiver para vencer). */
  accessTokenForSocket(): Promise<string>;
  login(login: string, password: string): Promise<void>;
  register(username: string, email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  matchHistory(limit?: number): Promise<MatchHistoryItem[]>;
  /** Faz uma chamada qualquer com a sessão (Bearer + renovação) para rotas que não estão nos contratos tipados. */
  authorizedFetch: typeof fetch;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const now = options.now ?? Date.now;
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  let refreshing: Promise<boolean> | null = null;

  const storeTokenResponse = (body: AuthSchemas['TokenResponse']): void => {
    options.tokens.set({
      accessToken: body.accessToken,
      refreshToken: body.refreshToken,
      accessExpiresAt: now() + Number(body.expiresIn) * 1000,
    });
  };

  /** Renovação única: várias chamadas que recebem 401 ao mesmo tempo esperam a mesma renovação (o refresh token só serve uma vez). */
  const refresh = (): Promise<boolean> => {
    refreshing ??= (async () => {
      const current = options.tokens.get();
      if (!current) return false;
      try {
        const response = await baseFetch(`${baseUrl}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
        });
        if (!response.ok) {
          // 401/400: a sessão acabou ou foi revogada. Erros de rede/5xx não derrubam a sessão: o refresh token ainda pode valer.
          if (response.status < 500) {
            options.tokens.set(null);
            options.onSessionExpired?.();
          }
          return false;
        }
        storeTokenResponse((await response.json()) as AuthSchemas['TokenResponse']);
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshing = null;
    });
    return refreshing;
  };

  const withAuth = (request: Request): Request => {
    const tokens = options.tokens.get();
    if (tokens && !request.headers.has('authorization')) request.headers.set('authorization', `Bearer ${tokens.accessToken}`);
    return request;
  };

  const authorizedFetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const original = new Request(input, init);
    const retry = original.clone();
    const sentWithToken = options.tokens.get() !== null;
    const first = await baseFetch(withAuth(original));
    const isAuthRoute = new URL(original.url, 'http://x').pathname.includes('/v1/auth/');
    if (first.status !== 401 || !sentWithToken || isAuthRoute) return first;
    if (!(await refresh())) return first;
    retry.headers.delete('authorization');
    return baseFetch(withAuth(retry));
  }) as typeof fetch;

  const auth = createClient<AuthPaths>({ baseUrl, fetch: authorizedFetch });
  const character = createClient<CharacterPaths>({ baseUrl, fetch: authorizedFetch });
  const quest = createClient<QuestPaths>({ baseUrl, fetch: authorizedFetch });

  return {
    auth,
    character,
    quest,
    tokens: options.tokens,
    authorizedFetch,

    async accessTokenForSocket() {
      const current = options.tokens.get();
      if (!current) throw new ApiError(401, 'unauthorized', 'Sessão ausente.');
      if (current.accessExpiresAt - REFRESH_MARGIN_MS <= now()) {
        if (!(await refresh())) throw new ApiError(401, 'session_expired', 'A sessão expirou.');
      }
      return options.tokens.get()!.accessToken;
    },

    async login(login, password) {
      const { data, error, response } = await auth.POST('/v1/auth/login', { body: { login, password } });
      if (!data) throw toApiError(response.status, error);
      storeTokenResponse(data);
    },

    async register(username, email, password) {
      const { error, response } = await auth.POST('/v1/accounts', { body: { username, email, password } });
      if (!response.ok) throw toApiError(response.status, error);
      await this.login(username, password);
    },

    async logout() {
      const current = options.tokens.get();
      options.tokens.set(null);
      if (current) {
        try {
          await auth.POST('/v1/auth/logout', { body: { refreshToken: current.refreshToken } });
        } catch {
          // Sem rede: a sessão local já foi apagada; o servidor expira o refresh token sozinho.
        }
      }
    },

    async matchHistory(limit = 20) {
      const response = await authorizedFetch(`${baseUrl}/v1/matches/me?limit=${limit}`);
      if (!response.ok) throw toApiError(response.status, await response.json().catch(() => null));
      return (await response.json()) as MatchHistoryItem[];
    },
  };
}
